import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from '@heroui/react';
import { Group, Hotel, Plus, Trash2, X } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useHeroToast, useWarmup } from '../../../hooks';
import {
  OrganizationGroup as OrgGroupModel,
  useAssignOrganizationToGroup,
  useDeleteOrganizationGroup,
  useGetOrganizationCollectionEndpoint,
  useGetOrganizationGroups,
} from '../../../queries';
import { AssignOrganizationsModal, CreateGroupModal } from '../components';

type AssignTarget = { id: string; name: string; assignedIds: string[] } | null;

export const OrganizationGroup: FC = () => {
  useWarmup([
    'v3-getOrganizationGroups',
    'v3-getOrganizationsCollection',
    'v3-createOrganizationGroup',
    'v3-updateOrganizationGroup',
    'v3-deleteOrganizationGroup',
    'v3-assignOrganizationToGroup',
    'v3-setOrganizationGroupMembers',
  ]);
  const { showSuccess, showError } = useHeroToast();
  const { data: groups, isLoading, error } = useGetOrganizationGroups();
  const { data: organizations, isLoading: isOrgsLoading } = useGetOrganizationCollectionEndpoint();
  const { mutate: assignToGroup } = useAssignOrganizationToGroup();
  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteOrganizationGroup();

  const [createOpen, setCreateOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<AssignTarget>(null);
  const [groupToDelete, setGroupToDelete] = useState<OrgGroupModel | null>(null);

  // Build maps:
  //   - groupIdToOrgs: groupId -> orgs. Primary source is org.groupId.
  //     Legacy fallback to group.assigned_organizations covers data
  //     created before we switched the model and also the case where
  //     the backend deploy that adds `groupId` to the response hasn't
  //     happened yet.
  //   - orgLookup: id -> display label
  const { groupIdToOrgs, orgLookup } = useMemo(() => {
    const orgs = organizations ?? [];
    const byGroup = new Map<string, typeof orgs>();
    const lookup = new Map<string, string>();
    orgs.forEach((o) => {
      lookup.set(o.id, o.hotel || o.name || o.code || o.id);
      if (o.groupId) {
        const list = byGroup.get(o.groupId) ?? [];
        list.push(o);
        byGroup.set(o.groupId, list);
      }
    });

    (groups ?? []).forEach((g) => {
      if (byGroup.has(g.id)) return;
      const legacyIds = g.assigned_organizations ?? [];
      if (legacyIds.length === 0) return;
      const list = legacyIds
        .map((id) => orgs.find((o) => o.id === id))
        .filter((o): o is typeof orgs[number] => !!o);
      if (list.length > 0) byGroup.set(g.id, list);
    });

    return { groupIdToOrgs: byGroup, orgLookup: lookup };
  }, [organizations, groups]);

  const handleRemoveOrgFromGroup = (orgId: string, groupId: string) => {
    assignToGroup(
      { organizationId: orgId, groupId: null, previousGroupId: groupId },
      {
        onSuccess: () => showSuccess('Usunięto z grupy'),
        onError: () => showError('Nie udało się zaktualizować grupy'),
      },
    );
  };

  const confirmDelete = () => {
    if (!groupToDelete) return;
    deleteGroup(
      { id: groupToDelete.id },
      {
        onSuccess: () => {
          showSuccess(`Usunięto grupę "${groupToDelete.name}"`);
          setGroupToDelete(null);
        },
        onError: () => showError('Nie udało się usunąć grupy'),
      },
    );
  };

  if (error) {
    return (
      <PageWrapper>
        <ContentLoadError message={error.message} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle
        title="Organizations group"
        buttonText="Utwórz grupę"
        buttonOnClick={() => setCreateOpen(true)}
      />

      {isLoading && (
        <div className="py-10 flex justify-center">
          <Spinner showLabel={false} />
        </div>
      )}

      {!isLoading && (groups ?? []).length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500">
          <Group size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm">Nie masz jeszcze żadnych grup organizacji.</p>
          <Button
            size="sm"
            color="primary"
            className="mt-4"
            startContent={<Plus size={14} />}
            onPress={() => setCreateOpen(true)}
          >
            Utwórz pierwszą grupę
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(groups ?? []).map((group) => {
          const groupOrgs = groupIdToOrgs.get(group.id) ?? [];
          return (
            <div
              key={group.id}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 bg-gradient-to-r from-primary/5 to-transparent">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Group size={16} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {group.name || '(bez nazwy)'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {isOrgsLoading ? (
                        '...'
                      ) : (
                        <>
                          {groupOrgs.length}
                          {' '}
                          {groupOrgs.length === 1 ? 'organizacja' : 'organizacji'}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Tooltip content="Usuń grupę" placement="left">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label="Usuń grupę"
                    onPress={() => setGroupToDelete(group)}
                  >
                    <Trash2 size={14} className="text-danger" />
                  </Button>
                </Tooltip>
              </div>

              {/* Content */}
              <div className="px-4 py-3 flex-1">
                {isOrgsLoading ? (
                  <div className="py-3 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner showLabel={false} />
                    <span>Ładowanie organizacji...</span>
                  </div>
                ) : groupOrgs.length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">
                    Brak przypisanych organizacji.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {groupOrgs.map((org) => (
                      <Chip
                        key={org.id}
                        size="sm"
                        variant="flat"
                        color="primary"
                        startContent={<Hotel size={10} />}
                        endContent={(
                          <button
                            type="button"
                            onClick={() => handleRemoveOrgFromGroup(org.id, group.id)}
                            className="ml-1 hover:opacity-70"
                            aria-label="Usuń z grupy"
                          >
                            <X size={11} />
                          </button>
                        )}
                      >
                        {orgLookup.get(org.id) || org.id}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/40">
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  startContent={<Plus size={14} />}
                  onPress={() => setAssignTarget({
                    id: group.id,
                    name: group.name,
                    assignedIds: groupOrgs.map((o) => o.id),
                  })}
                  fullWidth
                >
                  Przypisz organizacje
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <CreateGroupModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      <AssignOrganizationsModal
        isOpen={!!assignTarget}
        groupId={assignTarget?.id ?? null}
        groupName={assignTarget?.name}
        initialAssigned={assignTarget?.assignedIds ?? []}
        onClose={() => setAssignTarget(null)}
      />

      <Modal
        isOpen={!!groupToDelete}
        onOpenChange={(open) => { if (!open && !isDeleting) setGroupToDelete(null); }}
        size="sm"
        isDismissable={!isDeleting}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Usuń grupę?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-gray-600">
                  Ta operacja jest nieodwracalna. Czy na pewno chcesz usunąć grupę
                  {' '}
                  <span className="font-semibold">"{groupToDelete?.name}"</span>?
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={() => setGroupToDelete(null)} isDisabled={isDeleting}>
                  Anuluj
                </Button>
                <Button color="danger" onPress={confirmDelete} isLoading={isDeleting}>
                  Usuń
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
};
